import axios from "axios";

const CANDIDATE_BASE_URL = import.meta.env.VITE_API_BASE_URL as string;
let CANDIDATE_TENANT_ID = import.meta.env.VITE_X_TENANTID as string;

// Clean quotes from env variable if present (important for production builds)
if (CANDIDATE_TENANT_ID) {
  CANDIDATE_TENANT_ID = CANDIDATE_TENANT_ID.replace(/^["']|["']$/g, '').trim();
}

// Separate localStorage keys so candidate tokens don't clash with main app
const ACCESS_KEY = "candidate_accessToken";
const REFRESH_KEY = "candidate_refreshToken";
const USER_KEY = "candidate_userInfo";

export const getCandidateAccessToken = () =>
  localStorage.getItem(ACCESS_KEY) ?? null;
export const getCandidateRefreshToken = () =>
  localStorage.getItem(REFRESH_KEY) ?? null;
export const getCandidateUserInfo = () => {
  const v = localStorage.getItem(USER_KEY);
  return v ? JSON.parse(v) : null;
};
export const clearCandidateSession = () => {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
};

// Log configuration for debugging
console.log('🔧 Candidate Service Init:', {
  baseURL: CANDIDATE_BASE_URL,
  tenantId: CANDIDATE_TENANT_ID,
  rawEnv: import.meta.env.VITE_X_TENANTID
});

// Dedicated axios instance for candidate portal
const candidateAxios = axios.create({
  baseURL: CANDIDATE_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    "X-Tenant-Id": CANDIDATE_TENANT_ID,
  },
});

// Attach Bearer token automatically when available
candidateAxios.interceptors.request.use(config => {
  const token = getCandidateAccessToken();
  if (token && config.headers && !config.url?.includes("/auth/logout/")) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Ensure X-Tenant-Id is always present
  if (config.headers && CANDIDATE_TENANT_ID) {
    config.headers["X-Tenant-Id"] = CANDIDATE_TENANT_ID;
  }
  
  // Debug log for staging troubleshooting
  console.log('📤 Request:', {
    url: config.url,
    headers: {
      'X-Tenant-Id': config.headers?.["X-Tenant-Id"],
      'Authorization': config.headers?.Authorization ? '✅' : '❌'
    }
  });
  
  return config;
});

export interface CandidateUser {
  first_name: string;
  last_name: string;
  email: string;
  user_type: string;
}

export interface CandidateAuthResponse {
  access: string;
  refresh: string;
  user?: CandidateUser;
}

const CandidateAuthService = {
  login: async (
    email: string,
    password: string
  ): Promise<CandidateAuthResponse> => {
    const res = await candidateAxios.post<CandidateAuthResponse>(
      "/auth/login/",
      { email, password }
    );
    const { access, refresh, user } = res.data;
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
    return res.data;
  },

  signup: async (
    email: string,
    password: string,
    first_name: string,
    last_name: string
  ): Promise<CandidateAuthResponse> => {
    const res = await candidateAxios.post<CandidateAuthResponse>("/auth/", {
      email,
      password,
      first_name,
      last_name,
    });
    const { access, refresh, user } = res.data;
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
    return res.data;
  },

  logout: async (): Promise<void> => {
    const refresh = getCandidateRefreshToken();
    const access = getCandidateAccessToken();
    if (refresh && access) {
      await candidateAxios.post(
        "/auth/logout/",
        { refresh },
        { headers: { Authorization: `Bearer ${access}` } }
      );
    }
    clearCandidateSession();
  },
};

export default CandidateAuthService;
